import React, { useEffect, useState } from 'react';
import { fetchVisualRecords } from '../../servicios/apiClinica';

const fecha = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const rxTexto = (rec, ojo) => {
    const partes = [rec[`esfera_${ojo}`], rec[`cilindro_${ojo}`], rec[`eje_${ojo}`] ? `x ${rec[`eje_${ojo}`]}°` : '', rec[`add_${ojo}`] ? `ADD ${rec[`add_${ojo}`]}` : ''];
    return partes.filter(Boolean).join(' ') || '—';
};

const GENEROS = { M: 'Masculino', F: 'Femenino', O: 'Otro' };

const Dato = ({ label, children }) => (
    <div className="fp-dato">
        <span className="fp-dato-label">{label}</span>
        <span className="fp-dato-valor">{children || '—'}</span>
    </div>
);

// Ficha general del paciente: datos personales, estado del control visual y todas sus historias visuales.
const FichaPacienteModal = ({ patient, onClose, onVerHistoria, onEditarHistoria, onNuevoExamen, onPresupuesto }) => {
    const [historias, setHistorias] = useState(null);

    useEffect(() => {
        fetchVisualRecords(patient.id).then(recs => {
            setHistorias([...(recs || [])].sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at)));
        });
    }, [patient.id]);

    const ultima = historias?.find(h => !h.anulada);
    const alertas = Object.entries(patient.medical_alerts || {}).filter(([, v]) => v === true).map(([k]) => k.replace(/_/g, ' '));
    const vencido = patient.next_checkup_date && new Date(patient.next_checkup_date) < new Date();

    return (
        <div className="fp-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <style>{`
                .fp-overlay { position: fixed; inset: 0; background: rgba(17,24,39,0.7); z-index: 10040; display: flex; align-items: center; justify-content: center; padding: 16px; }
                .fp-modal { background: #fff; border-radius: 16px; width: 960px; max-width: 100%; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.3); }
                .fp-head { background: #111827; color: #fff; padding: 18px 24px; display: flex; align-items: center; gap: 16px; }
                .fp-avatar { width: 52px; height: 52px; border-radius: 50%; background: #b59a4a; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 1.2rem; flex-shrink: 0; }
                .fp-name { font-size: 1.25rem; font-weight: 800; margin: 0; }
                .fp-sub { color: #9ca3af; font-size: 0.85rem; margin-top: 2px; }
                .fp-close { margin-left: auto; background: transparent; border: none; color: #fff; font-size: 1.6rem; cursor: pointer; line-height: 1; }
                .fp-body { padding: 20px 24px; overflow-y: auto; background: #f9fafb; display: flex; flex-direction: column; gap: 16px; }
                .fp-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px 18px; }
                .fp-card h5 { margin: 0 0 12px; font-size: 0.8rem; font-weight: 800; letter-spacing: 0.06em; color: #6b7280; text-transform: uppercase; }
                .fp-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 20px; }
                .fp-dato { display: flex; flex-direction: column; min-width: 0; }
                .fp-dato-label { font-size: 0.72rem; color: #6b7280; font-weight: 600; text-transform: uppercase; }
                .fp-dato-valor { font-size: 0.92rem; color: #111827; font-weight: 600; overflow-wrap: anywhere; }
                .fp-chip { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.78rem; font-weight: 700; margin: 0 6px 6px 0; }
                .fp-rx { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
                .fp-rx th { text-align: left; color: #6b7280; font-weight: 600; font-size: 0.75rem; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
                .fp-rx td { padding: 8px; border-bottom: 1px solid #f3f4f6; color: #111827; vertical-align: top; }
                .fp-rx tr:last-child td { border-bottom: none; }
                .fp-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px; font-size: 0.82rem; font-weight: 700; cursor: pointer; border: 1.5px solid #111827; background: #fff; color: #111827; white-space: nowrap; }
                .fp-btn.fp-primary { background: #111827; color: #fff; }
                .fp-btn.fp-primary i, .fp-btn.fp-primary span { color: #fff; }
                .fp-actions { display: flex; gap: 8px; flex-wrap: wrap; }
                .fp-empty { color: #6b7280; font-size: 0.9rem; }
                @media (max-width: 720px) { .fp-grid { grid-template-columns: 1fr 1fr; } }
            `}</style>
            <div className="fp-modal">
                <div className="fp-head">
                    <div className="fp-avatar">{`${(patient.first_name || '?')[0]}${(patient.last_name || '')[0] || ''}`.toUpperCase()}</div>
                    <div>
                        <h4 className="fp-name">{patient.first_name} {patient.last_name}</h4>
                        <div className="fp-sub">C.I. {patient.cedula || 'S/C'} · {patient.age !== undefined && patient.age !== null ? `${patient.age} años` : 'Edad no registrada'}</div>
                    </div>
                    <button className="fp-close" onClick={onClose} title="Cerrar">&times;</button>
                </div>

                <div className="fp-body">
                    <div className="fp-card">
                        <h5>Datos personales</h5>
                        <div className="fp-grid">
                            <Dato label="Nombres">{patient.first_name}</Dato>
                            <Dato label="Apellidos">{patient.last_name}</Dato>
                            <Dato label="Cédula">{patient.cedula}</Dato>
                            <Dato label="Fecha de nacimiento">{patient.birth_date ? fecha(patient.birth_date) : ''}</Dato>
                            <Dato label="Género">{GENEROS[patient.gender]}</Dato>
                            <Dato label="Ocupación">{patient.occupation}</Dato>
                            <Dato label="Teléfono">{patient.phone}</Dato>
                            <Dato label="Correo">{patient.email}</Dato>
                            <Dato label="Dirección">{patient.address}</Dato>
                            {(patient.representative || patient.representative_cedula) && (
                                <>
                                    <Dato label="Representante">{patient.representative}</Dato>
                                    <Dato label="C.I. representante">{patient.representative_cedula}</Dato>
                                </>
                            )}
                            {patient.has_disability && <Dato label="Discapacidad">{patient.disability_description || 'Sí'}</Dato>}
                        </div>
                        {(alertas.length > 0 || patient.is_pregnant) && (
                            <div style={{ marginTop: '12px' }}>
                                {patient.is_pregnant && <span className="fp-chip" style={{ background: '#fce7f3', color: '#9d174d' }}>Embarazada</span>}
                                {alertas.map(a => <span key={a} className="fp-chip" style={{ background: '#fee2e2', color: '#991b1b' }}>{a}</span>)}
                            </div>
                        )}
                    </div>

                    <div className="fp-card">
                        <h5>Control visual</h5>
                        <div className="fp-grid">
                            <Dato label="Último control">{fecha(patient.last_checkup_date)}</Dato>
                            <Dato label="Próximo control">
                                <span style={{ color: vencido ? '#dc2626' : '#047857' }}>{fecha(patient.next_checkup_date)}{vencido ? ' (vencido)' : ''}</span>
                            </Dato>
                            <Dato label="Exámenes registrados">{historias ? String(historias.length) : '...'}</Dato>
                        </div>
                        {ultima && (
                            <div style={{ marginTop: '14px' }}>
                                <div className="fp-dato-label" style={{ marginBottom: '4px' }}>Fórmula vigente (Nº {ultima.folio_number} · {fecha(ultima.date)})</div>
                                <table className="fp-rx">
                                    <tbody>
                                        <tr><td style={{ width: '50px', fontWeight: 700 }}>O.D</td><td>{rxTexto(ultima, 'od')}</td><td>AVL {ultima.avl_od || '—'}</td></tr>
                                        <tr><td style={{ fontWeight: 700 }}>O.I</td><td>{rxTexto(ultima, 'oi')}</td><td>AVL {ultima.avl_oi || '—'}</td></tr>
                                    </tbody>
                                </table>
                                {(ultima.diagnostico || ultima.conducta) && (
                                    <div style={{ marginTop: '8px', fontSize: '0.88rem' }}>
                                        {ultima.diagnostico && <div><strong>Diagnóstico:</strong> {ultima.diagnostico}</div>}
                                        {ultima.conducta && <div><strong>Conducta:</strong> {ultima.conducta}</div>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="fp-card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px', flexWrap: 'wrap' }}>
                            <h5 style={{ margin: 0 }}>Historias clínicas visuales</h5>
                            <div className="fp-actions">
                                <button className="fp-btn" onClick={onPresupuesto}><i className="bi bi-receipt"></i><span>Presupuesto</span></button>
                                <button className="fp-btn fp-primary" onClick={onNuevoExamen}><i className="bi bi-plus-lg"></i><span>Nuevo examen</span></button>
                            </div>
                        </div>
                        {historias === null ? (
                            <div className="fp-empty">Cargando...</div>
                        ) : historias.length === 0 ? (
                            <div className="fp-empty">Este paciente aún no tiene historias clínicas visuales.</div>
                        ) : (
                            <table className="fp-rx">
                                <thead>
                                    <tr><th>Folio</th><th>Fecha</th><th>RX final</th><th>Profesional</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {historias.map(h => (
                                        <tr key={h.id}>
                                            <td style={{ fontWeight: 700, color: '#d2453b' }}>
                                                Nº {h.folio_number || '—'}
                                                {h.anulada && <div title={`Motivo: ${h.motivo_anulacion || '—'}`} style={{ fontSize: '0.7rem', color: '#991b1b', background: '#fee2e2', borderRadius: '999px', padding: '0 6px', display: 'inline-block' }}>ANULADA</div>}
                                            </td>
                                            <td>{fecha(h.date)}</td>
                                            <td>O.D {rxTexto(h, 'od')}<br />O.I {rxTexto(h, 'oi')}</td>
                                            <td>{h.profesional || '—'}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <div className="fp-actions" style={{ justifyContent: 'flex-end' }}>
                                                    <button className="fp-btn" onClick={() => onVerHistoria(h)}><i className="bi bi-eye"></i><span>Ver</span></button>
                                                    {!h.anulada && <button className="fp-btn" onClick={() => onEditarHistoria(h)}><i className="bi bi-pencil"></i><span>Editar</span></button>}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FichaPacienteModal;
